import useUser from 'utils/useUser';
import { useMemo, type FC } from 'react';
import {
  useAddActivityComment,
  useGenAiComment,
} from 'infrastructure/hooks/rpm';
import { useAppSelector } from 'store';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import { isCombinedModule } from 'infrastructure/functions';
import { useDeleteActivityComment } from 'infrastructure/hooks/rpm/activity/use-delete-comment';
import {
  ActivityStatus,
  ReadingStatus,
  type ModuleType,
} from 'infrastructure/enums';
import { useGetDoNotCallInfo } from 'infrastructure/hooks/control-panel/use-get-call-info';

import CommentsColumn from './components/comments-col';
import CommentColumn from './components/comment-col';
import ReviewedCheckbox from './components/reviewed-checkbox';
import s from './styles.module.scss';
import ErroneousWrapper from './components/erroneous-modal';

import type { TView } from './types';
import type { QueryKey } from '@tanstack/react-query';
import type { IActivityData } from 'infrastructure/interfaces/i-rpm-patient-activity';

interface CommentCellProps {
  createdAt: string;
  record: IActivityData;
  idx: number | string;
  showReviewCol: boolean;
  hideReviewCheckbox?: boolean;
  getActivityQueryKey: QueryKey;
  view: TView;
  module: ModuleType;
  type?: ActivityStatus;
}

const CommentCell: FC<CommentCellProps> = ({
  idx,
  record,
  showReviewCol,
  hideReviewCheckbox,
  getActivityQueryKey,
  view,
  module,
  type,
}) => {
  const { doNotCallRpm } = useGetDoNotCallInfo();

  const {
    userType,
    hasClinicalRole,
    isMedicalAssistantRole,
    isFullyManagedAgency,
    isAtRiskRole,
    isAgencyDoctor,
  } = useUser();
  const { isReadOnlyRole } = useCheckRoleAccess();

  const isReviewed = useMemo(() => {
    const reviewedByUserType = record.comments.find(
      (c) => c.reviewed,
    )?.userType;
    return reviewedByUserType === userType && record.reviewed;
  }, [record.comments.length]);
  const actions = useAppSelector((state) => state.activityActions.actions[idx]);

  const { addActivityComment, InteractionStatusAlert } = useAddActivityComment({
    getActivityQueryKey,
    patientGuid: record.patient.guid,
    tableRowIdx: idx,
  });

  const { deleteActivityComment } = useDeleteActivityComment({
    getActivityQueryKey,
    patientGuid: record.patient.guid,
    tableRowIdx: idx,
  });

  const {
    data: aiData,
    genAiComment,
    isCommentGenerationLimitReached,
    isLoading,
  } = useGenAiComment({
    patientGuid: record.patient.guid,
    tableRowIdx: idx,
  });

  const submitCommentLockByAi =
    Boolean(actions?.message) &&
    aiData?.result.comment === actions?.message &&
    hasClinicalRole;

  const showCommentColumn = isCombinedModule(module) ? !isReadOnlyRole : true;
  const showReviewColumn = showReviewCol && showCommentColumn;
  const isCriticalStatus = record.globalStatus === ReadingStatus.Critical;
  const disableForMARole =
    isCriticalStatus && isMedicalAssistantRole && !doNotCallRpm;

  // This is temporary solution, delete it after new dashboard release
  const disableForFullyManagedAgency =
    view === 'dashboard' &&
    isFullyManagedAgency &&
    isAgencyDoctor &&
    isAtRiskRole;

  const hideCommentArea =
    view === 'patient' &&
    isFullyManagedAgency &&
    isAgencyDoctor &&
    type === ActivityStatus.UnactionedNormalRisk;

  return (
    <>
      <div className={s['comments-cell']}>
        <div className={s['comments-wrapper']}>
          <CommentsColumn
            deleteComment={deleteActivityComment}
            comments={record.comments}
            patientGuid={record.patient.guid}
            erroneousReason={record.erroneousReason}
            erroneousMarkedAt={record.erroneousMarkedAt}
            erroneousAuthorName={record.erroneousAuthorName}
            isErroneous={record.isErroneous}
          />
          {showCommentColumn && !hideCommentArea && (
            <CommentColumn
              addComment={addActivityComment}
              genAiComment={genAiComment}
              deviceId={record.deviceId}
              readingId={record.id}
              idx={idx}
              key={`comment-col-${idx}`}
              createdAt={record.createdAt}
              showAiButton={showReviewCol}
              currentInteractionStatus={record.patient.interactionStatus}
              patientGuid={record.patient.guid}
              genAiCommentLoading={isLoading}
              isCommentGenerationLimitReached={isCommentGenerationLimitReached}
              aiData={aiData}
              isErroneous={record.isErroneous}
              thresholds={record.patient.threshold}
              values={record.value}
              statuses={record.statuses}
              readingType={record.readingType}
              globalStatus={record.globalStatus}
              thresholdName={record.thresholdName}
              disabled={disableForFullyManagedAgency}
              patientRpmDoNotCall={doNotCallRpm}
            />
          )}
          {showReviewColumn && (
            <div className={s.reviewed}>
              {view !== 'dashboard' && (
                <ErroneousWrapper
                  getActivityQueryKey={getActivityQueryKey}
                  deviceId={record.deviceId}
                  readingId={record.id}
                  isErroneous={record.isErroneous}
                  disabled={disableForMARole}
                />
              )}
              {!record.isErroneous && !hideReviewCheckbox && (
                <div className={s['checkbox-wrapper']}>
                  <ReviewedCheckbox
                    key={record.id + record.deviceId}
                    addComment={addActivityComment}
                    deviceId={record.deviceId}
                    readingId={record.id}
                    idx={idx}
                    currentInteractionStatus={record.patient.interactionStatus}
                    isReviewed={isReviewed}
                    submitCommentLockByAi={submitCommentLockByAi}
                    disabled={disableForMARole}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {InteractionStatusAlert}
    </>
  );
};
export default CommentCell;
